import {
  deliveryFeeDisplay,
  selectedDeliveryTotals,
} from 'src/utils/deliveryFeeDisplay'
import { Alert, Button, Checkbox, Modal, notification, Spin } from 'antd'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { formatDeliveryDays } from 'src/utils/deliveryDays'
import { countCheckoutLinesAndUnits } from 'src/utils/orderLineCounts'
import {
  fetchBasketLinesAddedByOthers,
  sharedBasketWarning,
} from 'src/utils/checkoutSharedBasket'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import debounce from 'lodash.debounce'
import dayjs from 'dayjs'
import { v4 as uuid } from 'uuid'
import NoDataScreen from 'src/components/shared/no-data-screen/NoDataScreen'
import FadeIn from 'src/components/styled/FadeIn'
import Loading from 'src/components/styled/Loading'
import styled from 'styled-components/macro'
import DuplicateWarning from './components/DuplicateWarning'
import OrderSummary from './components/OrderSummary'
import SupplierGroup from './components/SupplierGroup'
import DeliveryForm from './components/DeliveryForm'
import { getDuplicatePOValidationMessage } from './checkoutErrorMessages'
import GoodsReceiptRequiredModalContent from './components/GoodsReceiptRequiredModalContent'
import {
  GOODS_RECEIPT_REQUIRED_CODE,
  GOODS_RECEIPT_REQUIRED_TITLE,
  parseGoodsReceiptRequiredError,
} from 'src/utils/goodsReceiptReorderCopy'
import {
  fetchPendingOrderSummary,
  updateOrderQuantity,
  deleteOrder,
  confirmOrder,
} from 'src/features/OrdersSlice'
import { SuccessNotification } from 'src/notifications/SuccessNotification'
import Sentry from 'src/utils/sentry'
import {
  shouldShowCheckoutEmptyBasket,
  shouldShowCheckoutLoading,
} from 'src/utils/checkoutSummaryDisplay'

const CheckoutScreen = () => {
  const { venueId } = useParams()
  const dispatch = useDispatch()
  const history = useHistory()
  const checkoutSummary = useSelector(state => state.orders.checkoutSummary)
  const checkoutSummaryLoading = useSelector(
    state => state.orders.checkoutSummaryLoading,
  )
  const checkoutSummaryError = useSelector(
    state => state.orders.checkoutSummaryError,
  )
  const account = useSelector(state => state.auth.account)
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [deliveryDay, setDeliveryDay] = useState(null)
  const [poNumber, setPONumber] = useState(null)
  const [notes, setNotes] = useState(null)

  // Retry circuit breaker state (per session)
  const [retryCount, setRetryCount] = useState(0)
  const [retryBlockedUntil, setRetryBlockedUntil] = useState(null)

  // Double-submit prevention
  const [isSubmitting, setIsSubmitting] = useState(false)
  const confirmationRequest = useRef(null)

  // Initialize with empty structure if no data yet
  const [summaryData, setSummaryData] = useState({
    basketVersion: null,
    suppliers: [],
    duplicates: [],
    grandTotal: 0,
    totalSuppliers: 0,
    totalItems: 0,
  })
  const [selectedSuppliers, setSelectedSuppliers] = useState(new Set())
  const [loadingOrderIds, setLoadingOrderIds] = useState(new Set())
  const [errorOrderIds, setErrorOrderIds] = useState(new Map()) // Map<orderId, errorMessage>
  const [quantityEditsPending, setQuantityEditsPending] = useState(false)
  const pendingQuantityUpdates = useRef(new Map()) // Track pending debounced updates
  const viewSummary = summaryData

  // Fetch summary data on mount
  useEffect(() => {
    if (venueId) {
      dispatch(fetchPendingOrderSummary(venueId))
    }
  }, [dispatch, venueId])

  const [linesAddedByOthers, setLinesAddedByOthers] = useState(0)
  useEffect(() => {
    let cancelled = false
    const loadLinesAddedByOthers = async () => {
      const count = await fetchBasketLinesAddedByOthers(venueId)
      if (!cancelled) setLinesAddedByOthers(count)
    }
    loadLinesAddedByOthers()
    return () => {
      cancelled = true
    }
  }, [venueId, viewSummary.basketVersion])
  const sharedBasketText = sharedBasketWarning(linesAddedByOthers)

  const selectionInitialized = useRef(false)
  useEffect(() => {
    if (checkoutSummary) {
      setSummaryData(checkoutSummary)
      const supplierIds = checkoutSummary.suppliers.map(s => s.supplierId)
      const firstLoad = !selectionInitialized.current
      setSelectedSuppliers(prev =>
        firstLoad
          ? new Set(supplierIds)
          : new Set([...prev].filter(id => supplierIds.includes(id))),
      )
      selectionInitialized.current = true
    }
  }, [checkoutSummary])

  // Cleanup debounced functions on unmount
  useEffect(() => {
    return () => {
      pendingQuantityUpdates.current.forEach(fn => fn.cancel())
      pendingQuantityUpdates.current.clear()
    }
  }, [])

  // Calculate if all suppliers are selected
  const allSuppliersSelected = useMemo(() => {
    return (
      summaryData.suppliers.length > 0 &&
      selectedSuppliers.size === summaryData.suppliers.length
    )
  }, [selectedSuppliers, summaryData.suppliers])

  // Validate delivery day and show warning if invalid
  const validateDeliveryDay = useCallback(
    date => {
      if (!date) return false

      // Handle both ISO strings and moment objects
      const dateObj = typeof date === 'string' ? dayjs(date) : dayjs(date)
      if (!dateObj.isValid()) return false

      const day = dateObj.format('ddd').toLowerCase()
      const selectedSupplierData = summaryData.suppliers.filter(s =>
        selectedSuppliers.has(s.supplierId),
      )

      const warnings = []
      for (const supplier of selectedSupplierData) {
        if (
          supplier.deliveryDays &&
          supplier.deliveryDays.length > 0 &&
          !supplier.deliveryDays.includes(day)
        ) {
          warnings.push(supplier.supplierTitle)
        }
      }

      if (warnings.length === 0) return true

      // Show warning but allow order to proceed
      const supplierCount = warnings.length
      const isAllSuppliers = supplierCount === selectedSupplierData.length

      if (isAllSuppliers) {
        Modal.warning({
          title: 'Invalid Delivery Day',
          content: (
            <div>
              <p>
                The selected delivery date is not a standard delivery day for{' '}
                {supplierCount === 1 ? 'this supplier' : 'these suppliers'}.
              </p>
              <p>
                <strong>Standard delivery days:</strong>{' '}
                {formatDeliveryDays(selectedSupplierData[0]?.deliveryDays).join(
                  ', ',
                ) || 'N/A'}
              </p>
              <p>
                You can proceed with the order, but please be aware there is a
                risk that{' '}
                {supplierCount === 1 ? 'the supplier' : 'these suppliers'} may
                not be able to deliver on this date. We recommend contacting{' '}
                {supplierCount === 1 ? 'the supplier' : 'the suppliers'}{' '}
                directly to confirm availability.
              </p>
            </div>
          ),
          okText: 'Continue',
          width: 500,
        })
      } else {
        Modal.warning({
          title: 'Invalid Delivery Day',
          content: (
            <div>
              <p>
                The selected delivery date is not a standard delivery day for{' '}
                <strong>{warnings.join(', ')}</strong>.
              </p>
              <p>
                You can proceed with the order, but please be aware there is a
                risk that{' '}
                {supplierCount === 1 ? 'this supplier' : 'these suppliers'} may
                not be able to deliver on this date. We recommend contacting{' '}
                {supplierCount === 1 ? 'the supplier' : 'the suppliers'}{' '}
                directly to confirm availability.
              </p>
            </div>
          ),
          okText: 'Continue',
          width: 500,
        })
      }
      return true // Allow date selection
    },
    [summaryData.suppliers, selectedSuppliers],
  )

  // Handle supplier selection toggle
  const handleToggleSupplier = (supplierId, checked) => {
    const newSelected = new Set(selectedSuppliers)
    if (checked) {
      newSelected.add(supplierId)
    } else {
      newSelected.delete(supplierId)
    }
    setSelectedSuppliers(newSelected)
  }

  // Handle select all suppliers
  const handleSelectAllSuppliers = checked => {
    if (checked) {
      setSelectedSuppliers(
        new Set(summaryData.suppliers.map(s => s.supplierId)),
      )
    } else {
      setSelectedSuppliers(new Set())
    }
  }

  // Handle quantity change - debounced backend call
  const performQuantityUpdate = useCallback(
    async (orderId, quantity) => {
      // Prevent overlapping PATCH requests for the same order
      // If a PATCH is already in-flight, silently ignore this request
      // The in-flight request will complete and refetch will update the UI
      if (loadingOrderIds.has(orderId)) {
        return
      }

      // Cancel any pending update for this order
      if (pendingQuantityUpdates.current.has(orderId)) {
        pendingQuantityUpdates.current.get(orderId).cancel()
        pendingQuantityUpdates.current.delete(orderId)
        setQuantityEditsPending(pendingQuantityUpdates.current.size > 0)
      }

      setLoadingOrderIds(prev => new Set(prev).add(orderId))
      setErrorOrderIds(prev => {
        const newMap = new Map(prev)
        newMap.delete(orderId)
        return newMap
      })

      try {
        await dispatch(updateOrderQuantity(orderId, venueId, quantity))
        setLoadingOrderIds(prev => {
          const newSet = new Set(prev)
          newSet.delete(orderId)
          return newSet
        })
        pendingQuantityUpdates.current.delete(orderId)
        setQuantityEditsPending(pendingQuantityUpdates.current.size > 0)
      } catch (error) {
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          'Could not update quantity'
        setLoadingOrderIds(prev => {
          const newSet = new Set(prev)
          newSet.delete(orderId)
          return newSet
        })
        setErrorOrderIds(prev => {
          const newMap = new Map(prev)
          newMap.set(orderId, errorMessage)
          return newMap
        })
        notification.error({
          message: 'Update Failed',
          description: errorMessage,
          duration: 4,
        })
        pendingQuantityUpdates.current.delete(orderId)
        setQuantityEditsPending(pendingQuantityUpdates.current.size > 0)
      }
    },
    [dispatch, venueId, loadingOrderIds],
  )

  // Handle quantity change for an item - debounced backend call
  const handleQuantityChange = useCallback(
    (orderId, newQuantity) => {
      if (
        newQuantity === null ||
        newQuantity === undefined ||
        newQuantity < 0
      ) {
        return
      }

      // Cancel any pending update for this order
      if (pendingQuantityUpdates.current.has(orderId)) {
        pendingQuantityUpdates.current.get(orderId).cancel()
        pendingQuantityUpdates.current.delete(orderId)
        setQuantityEditsPending(pendingQuantityUpdates.current.size > 0)
      }

      // Create and store new debounced function for this order
      setQuantityEditsPending(true)
      const debouncedFn = debounce(() => {
        performQuantityUpdate(orderId, newQuantity)
      }, 300)

      pendingQuantityUpdates.current.set(orderId, debouncedFn)

      // Trigger debounced update
      debouncedFn()
    },
    [performQuantityUpdate],
  )

  // Handle item removal - immediate backend call
  const handleRemoveItem = async orderId => {
    // Cancel any pending quantity update for this order
    if (pendingQuantityUpdates.current.has(orderId)) {
      pendingQuantityUpdates.current.get(orderId).cancel()
      pendingQuantityUpdates.current.delete(orderId)
      setQuantityEditsPending(pendingQuantityUpdates.current.size > 0)
    }

    setLoadingOrderIds(prev => new Set(prev).add(orderId))
    setErrorOrderIds(prev => {
      const newMap = new Map(prev)
      newMap.delete(orderId)
      return newMap
    })

    try {
      await dispatch(deleteOrder(orderId, venueId))
      setLoadingOrderIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(orderId)
        return newSet
      })
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Could not delete item'
      setLoadingOrderIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(orderId)
        return newSet
      })
      setErrorOrderIds(prev => {
        const newMap = new Map(prev)
        newMap.set(orderId, errorMessage)
        return newMap
      })
      notification.error({
        message: 'Delete Failed',
        description: errorMessage,
        duration: 4,
      })
    }
  }

  // Retry failed operation
  const handleRetry = useCallback(
    async (orderId, operation, value) => {
      setErrorOrderIds(prev => {
        const newMap = new Map(prev)
        newMap.delete(orderId)
        return newMap
      })

      // Get current quantity from summaryData if not provided
      let currentQuantity = value
      if (!currentQuantity) {
        for (const supplier of summaryData.suppliers) {
          const item = supplier.items.find(item => item.orderId === orderId)
          if (item) {
            currentQuantity = item.quantity
            break
          }
        }
      }

      if (operation === 'update' && currentQuantity !== undefined) {
        await performQuantityUpdate(orderId, currentQuantity)
      } else if (operation === 'delete') {
        await handleRemoveItem(orderId)
      }
    },
    [performQuantityUpdate, summaryData, handleRemoveItem],
  )

  // Recalculate grand total when suppliers selection changes
  const selectedTotals = useMemo(
    () => selectedDeliveryTotals(summaryData.suppliers, selectedSuppliers),
    [selectedSuppliers, summaryData.suppliers],
  )
  const grandTotal = selectedTotals.total
  const blockedSuppliers = summaryData.suppliers.filter(
    s => selectedSuppliers.has(s.supplierId) && !deliveryFeeDisplay(s).canOrder,
  )
  const totalsUpdating =
    checkoutSummaryLoading || quantityEditsPending || loadingOrderIds.size > 0
  const reviewReady =
    !totalsUpdating &&
    !checkoutSummaryError &&
    errorOrderIds.size === 0 &&
    /^[a-f0-9]{64}$/.test(summaryData.basketVersion || '')

  const multiSupplierSelected = selectedSuppliers.size > 1

  // Custom PO is only valid for a single supplier — clear it when multi-selected.
  useEffect(() => {
    if (multiSupplierSelected && poNumber) {
      setPONumber(null)
    }
  }, [multiSupplierSelected, poNumber])

  // Handle confirm order (Phase 3)
  const handleConfirmOrder = async () => {
    // Double-submit prevention
    if (isSubmitting || confirmLoading) {
      return
    }

    if (selectedSuppliers.size === 0) {
      notification.warning({
        message: 'No Suppliers Selected',
        description: 'Please select at least one supplier to continue.',
      })
      return
    }

    if (!summaryData || summaryData.totalItems === 0) {
      notification.warning({
        message: 'Basket is Empty',
        description: 'Please add items to your basket before confirming.',
      })
      return
    }

    // Validate delivery day is provided
    if (!deliveryDay) {
      Modal.error({
        title: 'Delivery Day Required',
        content: 'Please select a delivery day before confirming your order.',
        okText: 'OK',
      })
      return
    }

    // Validate delivery day and show warning if invalid (but allow proceeding)
    validateDeliveryDay(deliveryDay)

    // Check retry circuit breaker
    const now = Date.now()
    if (retryBlockedUntil && now < retryBlockedUntil) {
      const remainingSeconds = Math.ceil((retryBlockedUntil - now) / 1000)
      Modal.error({
        title: 'Too Many Retries',
        content: `Please wait ${remainingSeconds} second${
          remainingSeconds !== 1 ? 's' : ''
        } before trying again.`,
        okText: 'OK',
      })
      return
    }

    if (!reviewReady || blockedSuppliers.length > 0) {
      Modal.warning({
        title: 'Review your basket',
        content:
          blockedSuppliers
            .map(s => `${s.supplierTitle}: ${deliveryFeeDisplay(s).message}`)
            .join(' \n') ||
          'Wait for totals to update, or refresh and review your basket.',
      })
      return
    }

    // Set submitting state immediately
    setIsSubmitting(true)
    setConfirmLoading(true)

    try {
      const filteredSelectedSuppliers = Array.from(selectedSuppliers).filter(
        id => summaryData.suppliers.some(s => s.supplierId === id),
      )

      if (filteredSelectedSuppliers.length === 0) {
        Modal.error({
          title: 'No Valid Suppliers',
          content:
            'Please select at least one supplier that has items in your basket.',
          okText: 'OK',
          cancelButtonProps: { style: { display: 'none' } },
        })
        setIsSubmitting(false)
        setConfirmLoading(false)
        return
      }

      const body = {
        selectedSuppliers: filteredSelectedSuppliers,
        notes: notes,
        requiredDeliveryDay: dayjs(deliveryDay).format('YYYY-MM-DD'),
        placedVia: 'dashboard',
        placedBy: account.emailAddress || null,
        // Multi-supplier checkouts must auto-generate a unique PO per supplier.
        purchaseOrderNumber: multiSupplierSelected ? null : poNumber || null,
        basketVersion: summaryData.basketVersion,
      }
      const requestKey = JSON.stringify(body)
      if (confirmationRequest.current?.key !== requestKey) {
        confirmationRequest.current = { key: requestKey, token: uuid() }
      }
      body.requestToken = confirmationRequest.current.token

      const result = await dispatch(confirmOrder(venueId, body))
      if (result?.code !== 'SUCCESS') {
        throw Object.assign(
          new Error(result?.message || 'Failed to confirm order'),
          { code: result?.code },
        )
      }
      const apiSuppliers = result.suppliers || []
      const supplierCount = result.supplierCount || apiSuppliers.length
      const restrictedCount = apiSuppliers.filter(
        s => s.restricted === true,
      ).length
      const hasRestrictedSuppliers = restrictedCount > 0

      // Reset retry count on success
      setRetryCount(0)
      setRetryBlockedUntil(null)

      setConfirmLoading(false)
      setIsSubmitting(false)

      // Log to Sentry for observability
      Sentry.captureMessage('Dashboard order submitted', {
        level: 'info',
        extra: {
          venueId,
          restrictedCount,
          supplierCount,
          hasRestrictedSuppliers,
          source: 'dashboard',
        },
      })

      // Delete confirmed orders from the basket
      // IMPORTANT: Only delete orders from UNRESTRICTED suppliers
      // Restricted supplier orders must remain in DB with approvalStatus: 'pending'
      // const orderIdsToDelete = []
      const restrictedSupplierIds = new Set(
        apiSuppliers.filter(s => s.restricted === true).map(s => s.supplierId),
      )

      console.log('🔵 [CheckoutScreen] Determining which orders to delete:', {
        hasRestrictedSuppliers,
        restrictedSupplierIds: Array.from(restrictedSupplierIds),
        apiSuppliers: apiSuppliers.map(s => ({
          supplierId: s.supplierId,
          restricted: s.restricted,
        })),
      })

      // for (const supplier of summaryData.suppliers || []) {
      //   if (selectedSuppliers.has(supplier.supplierId)) {
      //     // Only delete orders from unrestricted suppliers
      //     const isRestricted = restrictedSupplierIds.has(supplier.supplierId)

      //     if (!isRestricted) {
      //       // Unrestricted supplier: delete orders (they've been sent to supplier)
      //       for (const item of supplier.items || []) {
      //         if (item.orderId) {
      //           orderIdsToDelete.push(item.orderId)
      //         }
      //       }
      //     } else {
      //       // Restricted supplier: DO NOT delete orders (they're pending approval)
      //       console.log(
      //         '🔵 [CheckoutScreen] Skipping deletion for restricted supplier:',
      //         {
      //           supplierId: supplier.supplierId,
      //           supplierTitle: supplier.supplierTitle,
      //           itemCount: supplier.items?.length || 0,
      //         },
      //       )
      //     }
      //   }
      // }

      // // Delete orders in parallel (but don't fail if some fail)
      // if (orderIdsToDelete.length > 0) {
      //   console.log('🔵 [CheckoutScreen] Deleting orders from basket:', {
      //     count: orderIdsToDelete.length,
      //     orderIds: orderIdsToDelete,
      //   })
      //   const deletePromises = orderIdsToDelete.map(orderId =>
      //     dispatch(deleteOrder(orderId, venueId)).catch(err => {
      //       console.error(`Failed to delete order ${orderId}:`, err)
      //       // Don't throw - continue with other deletions
      //     }),
      //   )
      //   await Promise.all(deletePromises)
      // } else {
      //   console.log(
      //     '🔵 [CheckoutScreen] No orders to delete (all suppliers are restricted)',
      //   )
      // }

      if (hasRestrictedSuppliers && restrictedCount > 0) {
        // Restricted suppliers present: keep current behaviour (toast + Orders screen)
        SuccessNotification({
          message: 'Order submitted',
          description: `${restrictedCount} supplier${
            restrictedCount !== 1 ? 's are' : ' is'
          } awaiting manager approval. You will be notified once a decision is made.`,
        })
        // Show success screen for restricted suppliers too
      } else {
        // No restricted suppliers: show success screen with normal messaging
        SuccessNotification({
          message: 'Order sent successfully',
          description: `Your order has been sent to ${supplierCount} supplier${
            supplierCount !== 1 ? 's' : ''
          }.`,
        })
      }

      // Always show success screen (for both restricted and unrestricted)
      history.push(`/v/${venueId}/checkout/success`, {
        orderData: summaryData,
        selectedSuppliers: filteredSelectedSuppliers,
        result: result,
      })
    } catch (error) {
      setConfirmLoading(false)
      setIsSubmitting(false)

      // Keep the same reviewed payload/token on an uncertain network result.
      const isTimeout =
        ['ECONNABORTED', 'ETIMEDOUT', 'ERR_NETWORK'].includes(error.code) ||
        (!error.response && !error.data && !error.code) ||
        error.message?.toLowerCase().includes('timeout')
      if (isTimeout) {
        Modal.warning({
          title: 'Order status unconfirmed',
          content:
            'We could not verify whether your order was confirmed. Retry this confirmation, or check Orders before placing another order.',
          okText: 'Retry confirmation',
          onOk: () => handleConfirmOrder(),
          cancelText: 'Close',
        })
        return
      }

      // Handle structured error responses
      const errorData = error.response?.data || error.data || {}
      const errorCode = errorData.code || error.code || 'UNKNOWN_ERROR'
      const errorMessage = errorData.message || 'Failed to confirm order'
      const errorDetails = errorData.errors || errorData

      switch (errorCode) {
        case 'BASKET_CHANGED':
        case 'INVALID_BASKET_VERSION':
        case 'STALE_BASKET':
          Modal.error({
            title: 'Basket Changed',
            content:
              'Your basket changed while you were reviewing it. Please refresh and review again.',
            okText: 'Refresh',
            onOk: () => {
              dispatch(fetchPendingOrderSummary(venueId))
            },
            cancelButtonProps: { style: { display: 'none' } },
          })
          break

        case 'DUPLICATES_PRESENT':
          Modal.error({
            title: 'Duplicate Products Detected',
            content:
              'Please resolve duplicate products in your basket before confirming.',
            okText: 'OK',
            onOk: () => {
              // Scroll to duplicates section
              const duplicateSection = document.querySelector(
                '[data-duplicate-warning]',
              )
              if (duplicateSection) {
                duplicateSection.scrollIntoView({ behavior: 'smooth' })
              }
            },
            cancelButtonProps: { style: { display: 'none' } },
          })
          break

        case 'BELOW_MINIMUM_ORDER': {
          const minOrderError = Array.isArray(errorDetails)
            ? errorDetails[0]
            : errorDetails
          Modal.error({
            title: 'Minimum Order Not Met',
            content: (
              <div>
                <p>
                  <strong>{minOrderError?.supplierTitle || 'Supplier'}</strong>{' '}
                  requires a minimum order of{' '}
                  <strong>
                    £{minOrderError?.minOrderValue?.toFixed(2) || '0.00'}
                  </strong>
                </p>
                <p>
                  Current total:{' '}
                  <strong>
                    £{minOrderError?.currentTotal?.toFixed(2) || '0.00'}
                  </strong>
                </p>
              </div>
            ),
            okText: 'OK',
            cancelButtonProps: { style: { display: 'none' } },
          })
          break
        }

        case 'INVALID_DELIVERY_DAY': {
          // This error should no longer occur since backend validation was removed
          // Keeping handler for defensive programming, but show as warning not error
          const deliveryError = Array.isArray(errorDetails)
            ? errorDetails[0]
            : errorDetails
          const allowedDays = deliveryError?.allowedDays || []

          Modal.warning({
            title: 'Delivery Day Warning',
            content: (
              <div>
                <p>
                  <strong>{deliveryError?.supplierTitle || 'Supplier'}</strong>{' '}
                  doesn&apos;t typically deliver on the selected date.
                </p>
                {allowedDays.length > 0 && (
                  <p>
                    Typical delivery days:{' '}
                    <strong>{allowedDays.join(', ')}</strong>
                  </p>
                )}
                <p style={{ marginTop: '16px', fontStyle: 'italic' }}>
                  The order will still be sent. You may want to contact the
                  supplier directly to confirm.
                </p>
              </div>
            ),
            okText: 'Continue',
          })
          // Don't break - allow order to continue
          break
        }

        case 'SUPPLIER_CANNOT_RECEIVE_ORDERS':
        case 'NO_SUPPLIERS_SELECTED':
        case 'SUPPLIER_HAS_NO_ITEMS':
        case 'ORDERS_INVALID':
        case 'ORDER_ALREADY_SENT':
        case 'SUPPLIER_ARRAY_CONTAINS_DUPLICATES':
        case 'INVALID_PO_NUMBER':
          Modal.error({
            title: 'Validation Failed',
            content: errorMessage,
            okText: 'OK',
            cancelButtonProps: { style: { display: 'none' } },
          })
          break

        case 'DUPLICATE_PO_NUMBER':
          Modal.error({
            title: 'PO Number Already Used',
            content: getDuplicatePOValidationMessage(errorMessage),
            okText: 'OK',
            cancelButtonProps: { style: { display: 'none' } },
          })
          break

        case GOODS_RECEIPT_REQUIRED_CODE: {
          // Basket is not cleared on sendOrder failure — keep user on checkout.
          const parsed = parseGoodsReceiptRequiredError(errorData)
          Modal.confirm({
            title: GOODS_RECEIPT_REQUIRED_TITLE,
            width: 520,
            icon: null,
            content: (
              <GoodsReceiptRequiredModalContent
                supplierTitle={parsed.supplierTitle}
                backendMessage={parsed.message || errorMessage}
              />
            ),
            okText: 'Go to Goods Receipting',
            cancelText: 'Return to Basket',
            onOk: () => {
              history.push(`/v/${venueId}/purchasing/receipting`)
            },
            onCancel: () => {
              // Stay on checkout so the user can submit again after receipting.
            },
          })
          break
        }

        case 'IN_PROGRESS':
          Modal.info({
            title: 'Order Confirmation In Progress',
            content:
              'Your order confirmation is already being processed. Please wait a moment.',
            okText: 'OK',
          })
          break

        case 'TRANSACTION_FAILED':
        case 'TRANSACTION_NOT_SUPPORTED': {
          // Check retry circuit breaker
          const now = Date.now()
          const canRetry = !retryBlockedUntil || now >= retryBlockedUntil

          if (!canRetry) {
            const remainingSeconds = Math.ceil((retryBlockedUntil - now) / 1000)
            Modal.error({
              title: 'Too Many Retries',
              content: `Please wait ${remainingSeconds} second${
                remainingSeconds !== 1 ? 's' : ''
              } before trying again.`,
              okText: 'OK',
            })
            break
          }

          // Increment retry count
          const newRetryCount = retryCount + 1
          setRetryCount(newRetryCount)

          // Block retry if 3 or more attempts
          if (newRetryCount >= 3) {
            setRetryBlockedUntil(now + 60 * 1000) // Block for 60 seconds
            Modal.error({
              title: 'Too Many Retries',
              content:
                'You have exceeded the maximum number of retry attempts. Please wait 60 seconds before trying again.',
              okText: 'OK',
            })
            break
          }

          Modal.error({
            title: 'Order Confirmation Failed',
            content: (
              <div>
                <p>{errorMessage}</p>
                <p
                  style={{ fontSize: '12px', color: '#999', marginTop: '10px' }}
                >
                  Your basket has been preserved. Please try again.
                </p>
                <p
                  style={{ fontSize: '12px', color: '#999', marginTop: '5px' }}
                >
                  Retry attempt {newRetryCount} of 3
                </p>
              </div>
            ),
            okText: 'Retry',
            onOk: async () => {
              // Refetch summary first
              await dispatch(fetchPendingOrderSummary(venueId))
              // Then retry confirm
              handleConfirmOrder()
            },
            cancelText: 'Cancel',
            onCancel: () => {
              // Reset retry count on cancel
              setRetryCount(0)
              setRetryBlockedUntil(null)
            },
          })
          break
        }

        case 'INTERNAL_ERROR':
        default:
          Modal.error({
            title: 'Order Confirmation Failed',
            content: errorMessage,
            okText: 'OK',
            cancelButtonProps: { style: { display: 'none' } },
          })
      }
    }
  }

  // Get selected suppliers count for display
  const selectedSuppliersCount = selectedSuppliers.size
  const viewSuppliers = Array.isArray(viewSummary.suppliers)
    ? viewSummary.suppliers
    : []
  const { lines: productLineCount, units: productUnitCount } =
    countCheckoutLinesAndUnits(viewSuppliers, selectedSuppliers)
  const totalItemsCount = viewSummary.totalItems || 0
  const hasSuppliers = viewSuppliers.length > 0
  const canConfirm =
    selectedSuppliersCount > 0 &&
    hasSuppliers &&
    totalItemsCount > 0 &&
    reviewReady &&
    blockedSuppliers.length === 0

  if (
    shouldShowCheckoutLoading({
      loading: checkoutSummaryLoading,
      summary: checkoutSummary,
      error: checkoutSummaryError,
    })
  ) {
    return (
      <Wrapper>
        <Loading />
      </Wrapper>
    )
  }

  // Handle error state
  if (checkoutSummaryError) {
    return (
      <Wrapper>
        <ErrorContainer>
          <ErrorTitle>Error loading checkout summary</ErrorTitle>
          <ErrorMessage>{checkoutSummaryError}</ErrorMessage>
          <Button
            type='primary'
            onClick={() => {
              if (venueId) {
                dispatch(fetchPendingOrderSummary(venueId))
              }
            }}
          >
            Retry
          </Button>
        </ErrorContainer>
      </Wrapper>
    )
  }

  if (
    shouldShowCheckoutEmptyBasket({
      loading: checkoutSummaryLoading,
      summary: checkoutSummary,
      error: checkoutSummaryError,
    })
  ) {
    return (
      <Wrapper>
        <NoDataScreen page='basket' />
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      {/* Loading overlay during confirm */}
      {confirmLoading && (
        <LoadingOverlay>
          <Spin size='large' tip='Confirming order...' />
        </LoadingOverlay>
      )}

      <Header>
        <Title>Checkout</Title>
        <Description>
          <p>
            <strong>Final review before you send your order.</strong>
          </p>
          <p>
            This screen shows <em>everything</em> OMS is about to send to your
            suppliers.
          </p>
          <p>
            If multiple users or devices add items during the day, they will
            appear here.
          </p>
          <p>
            Please double-check the full order below and make sure all
            quantities and suppliers are correct before confirming.
          </p>
        </Description>
      </Header>

      <Content>
        {sharedBasketText ? (
          <WarningBanner>⚠️ {sharedBasketText}</WarningBanner>
        ) : null}

        {viewSummary.duplicates && viewSummary.duplicates.length > 0 && (
          <div data-duplicate-warning>
            <DuplicateWarning duplicates={viewSummary.duplicates} />
          </div>
        )}

        <SelectAllContainer>
          <Checkbox
            checked={allSuppliersSelected}
            indeterminate={selectedSuppliers.size > 0 && !allSuppliersSelected}
            onChange={e => handleSelectAllSuppliers(e.target.checked)}
          >
            <SelectAllText>Select All Suppliers</SelectAllText>
          </Checkbox>
        </SelectAllContainer>

        {viewSuppliers.map(supplier => (
          <SupplierGroup
            key={supplier.supplierId}
            supplier={supplier}
            isSelected={selectedSuppliers.has(supplier.supplierId)}
            onToggleSelection={handleToggleSupplier}
            onQuantityChange={handleQuantityChange}
            onRemoveItem={handleRemoveItem}
            duplicates={viewSummary.duplicates || []}
            isPricesHidden={false}
            loadingOrderIds={loadingOrderIds}
            errorOrderIds={errorOrderIds}
            onRetry={handleRetry}
            isRefetching={totalsUpdating}
          />
        ))}

        <DeliveryForm
          requiredDeliveryDay={deliveryDay}
          purchaseOrderNumber={poNumber}
          notes={notes}
          onDeliveryDayChange={setDeliveryDay}
          onPONumberChange={setPONumber}
          onNotesChange={setNotes}
          validateDeliveryDay={validateDeliveryDay}
          multiSupplierSelected={multiSupplierSelected}
        />
      </Content>

      {checkoutSummaryError && (
        <Alert
          type='error'
          message='Could not update totals. Refresh your basket before confirming.'
          action={
            <Button onClick={() => dispatch(fetchPendingOrderSummary(venueId))}>
              Refresh
            </Button>
          }
        />
      )}
      <Footer>
        <OrderSummary
          grandTotal={grandTotal}
          goodsTotal={selectedTotals.goods}
          deliveryTotal={selectedTotals.delivery}
          totalSuppliers={selectedSuppliersCount}
          totalItems={productLineCount}
          totalUnits={productUnitCount}
          isRefetching={totalsUpdating}
        />
        <ConfirmButton
          type='primary'
          size='large'
          disabled={!canConfirm || confirmLoading || isSubmitting}
          loading={confirmLoading || isSubmitting}
          onClick={handleConfirmOrder}
        >
          {!canConfirm && selectedSuppliersCount === 0
            ? 'Please select at least one supplier'
            : !canConfirm && totalItemsCount === 0
            ? 'Basket is empty'
            : totalsUpdating
            ? 'Updating totals…'
            : blockedSuppliers.length > 0
            ? 'Minimum order not met'
            : !reviewReady
            ? 'Refresh basket to continue'
            : 'Confirm Order'}
        </ConfirmButton>
      </Footer>
    </Wrapper>
  )
}

const Wrapper = styled(FadeIn)`
  width: 100%;
  min-height: 100vh;
  padding-bottom: 160px; // Space for fixed footer
  position: relative;
`

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`

const Header = styled.div`
  margin-bottom: 30px;
  padding: 20px 0;
`

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #333;
  margin: 0 0 10px 0;
`

const Description = styled.div`
  font-size: 16px;
  color: #666;
  margin: 0;

  p {
    margin: 0 0 12px 0;
    line-height: 1.6;

    &:last-child {
      margin-bottom: 0;
    }

    strong {
      font-weight: 600;
      color: #333;
    }

    em {
      font-style: italic;
    }
  }
`

const WarningBanner = styled.div`
  background: #fff7d6;
  border: 1px solid #f2d487;
  padding: 12px 16px;
  border-radius: 6px;
  margin-top: 24px;
  margin-bottom: 24px;
  font-size: 14px;
  color: #333;
`

const Content = styled.div`
  width: 100%;
`

const SelectAllContainer = styled.div`
  margin-bottom: 20px;
  padding: 15px;
  background: #f5f5f5;
  border-radius: 4px;
`

const SelectAllText = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #333;
`

const Footer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  min-height: 80px;
  gap: 16px;
  flex-wrap: wrap;
  background: ${({ theme }) => theme.colours.avocadoGreen};
  padding: 12px 100px;
  @media (max-width: 1000px) {
    padding: 12px 16px;
  }
  display: flex;
  justify-content: space-between;
  align-items: center;
  z-index: 1000;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
`

const ConfirmButton = styled(Button)`
  height: 45px;
  font-size: 16px;
  font-weight: 600;
  padding: 0 30px;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 20px;
`

const ErrorTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0;
`

const ErrorMessage = styled.p`
  font-size: 16px;
  color: #666;
  margin: 0;
`

export default CheckoutScreen
